import React, { useCallback, useEffect, useRef, useState } from "react";
import { Portal } from "@mantine/core";

interface UsePrintDocumentOptions {
  /** An external image (e.g. the business logo) the printout shows. Printing
   *  waits for it to load (or `IMAGE_LOAD_TIMEOUT_MS`) so it isn't blank. */
  waitForImageUrl?: string | null;
}

interface UsePrintDocument<T> {
  /** Print one document — mounts a single hidden print copy rendered from
   *  `data` and opens the browser/OS print dialog once it (and the image) are
   *  ready. */
  print: (data: T) => void;
  /** Render this ONCE at the surface root. It is the single hidden `.printOnly`
   *  document — never render it per-item, or every copy would print at once. */
  printNode: React.ReactNode;
}

// Give an external image a beat to load before printing so it isn't blank;
// print anyway once it resolves or this elapses.
const IMAGE_LOAD_TIMEOUT_MS = 1200;
// Safety net if `afterprint` never fires (some mobile share-sheet flows).
const AFTERPRINT_FALLBACK_MS = 60000;

/**
 * Generic on-demand printing (gift-card certificates, transaction receipts).
 * Instantiate it ONCE per surface and render `printNode` a single time; call
 * `print(data)` to print. `render` must return a print-only subtree (a
 * `.printOnly` root revealed by its CSS module's `@media print` rule).
 *
 * The single-mount design is load-bearing: the `@media print` rule reveals every
 * mounted `.printOnly` subtree, so a surface must share ONE hidden document
 * (this hook) rather than mounting one per item. The document is only mounted
 * while a print is pending and is cleared on `afterprint`.
 */
export default function usePrintDocument<T>(
  render: (data: T) => React.ReactNode,
  { waitForImageUrl = null }: UsePrintDocumentOptions = {},
): UsePrintDocument<T> {
  // Boxed so a `T` that is itself a function can't be mistaken for an updater.
  const [pending, setPending] = useState<{ data: T } | null>(null);
  const printedRef = useRef(false);
  // Latest image URL, read when the print fires. Kept out of the effect's deps
  // so a change mid-print (e.g. a SalonContext refresh while the dialog is
  // open) can't tear down the in-flight print's `afterprint` cleanup.
  const imageUrlRef = useRef(waitForImageUrl);
  imageUrlRef.current = waitForImageUrl;

  const print = useCallback((data: T) => {
    printedRef.current = false;
    setPending({ data });
  }, []);

  // Once the hidden document is committed to the DOM, wait a paint (and the
  // image's load) then open the print dialog. Clear the mount on `afterprint`.
  useEffect(() => {
    if (!pending || printedRef.current) return;

    let cancelled = false;
    let imageTimer: ReturnType<typeof setTimeout> | undefined;

    const doPrint = () => {
      if (cancelled || printedRef.current) return;
      printedRef.current = true;
      window.print();
    };

    const afterPrint = () => setPending(null);
    window.addEventListener("afterprint", afterPrint);

    const raf = requestAnimationFrame(() => {
      if (cancelled) return;
      const imageUrl = imageUrlRef.current;
      if (!imageUrl) {
        doPrint();
        return;
      }
      // Gate on the image so an un-cached external image isn't blank in print.
      const img = new window.Image();
      let settled = false;
      const go = () => {
        if (settled) return;
        settled = true;
        doPrint();
      };
      img.onload = go;
      img.onerror = go;
      img.src = imageUrl;
      if (img.complete) go();
      imageTimer = setTimeout(go, IMAGE_LOAD_TIMEOUT_MS);
    });

    const fallbackTimer = setTimeout(() => {
      if (!cancelled) setPending(null);
    }, AFTERPRINT_FALLBACK_MS);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (imageTimer) clearTimeout(imageTimer);
      clearTimeout(fallbackTimer);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [pending]);

  // Rendered through a Portal (into document.body) so the print subtree's
  // `position: absolute; left: 0` resolves against the page, not the positioned/
  // transformed container of a Mantine Modal or drawer it may be mounted inside —
  // which is what skewed printouts to the right. The `@media print` visibility
  // flip still applies (the portaled node is under `body`, hidden then re-shown
  // as `.printOnly`).
  const printNode = pending ? <Portal>{render(pending.data)}</Portal> : null;

  return { print, printNode };
}
